import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import moment from 'moment/moment';
import t360IntegrationStore from '~/view/screens/SettingsScreen/store/t360IntegrationStore';
import { getWeekdays } from '~/utils/helpers';
import Text from '~/view/components/Text/Text';
import WorkingHoursSlots from '~/view/screens/BranchesManagementScreen/screens/BranchesWorkingHours/components/TimeSlots/TimeSlots';
import { TBranchWorkingTime } from '~/view/screens/BranchesManagementScreen/screens/BranchesWorkingHours/types';
import { TWorkingTime } from '~/types/workingTime';
import './style.scss';
import { getNextDayOfWeek } from '~/helpers/getNextDayOfWeek/getNextDayOfWeek';
import { RESTRICTION_ACCESS } from '~/constants/integrations';

interface IProps {
  integrationId: number;
  disabled?: boolean;
}

const T360Timeslots: React.FC<IProps> = observer((props: IProps) => {
  const integrationSettingsTemp = useMemo(
    () => t360IntegrationStore.integrationsSettingsTemp[props.integrationId],
    [t360IntegrationStore.integrationsSettingsTemp]
  );

  const selectedTimeslots = useMemo(
    () =>
      (integrationSettingsTemp?.settings.timeslots ?? []).map(
        (timeslot: any, idx: number): TBranchWorkingTime => ({
          id: idx,
          startTime: timeslot.start_time,
          closeTime: timeslot.close_time,
          weekday: getWeekdays()[timeslot.weekday],
          isNew: false,
          menuGroupId: undefined,
          menuGroupName: undefined,
          orderType: undefined,
        })
      ),
    [t360IntegrationStore.integrationsSettingsTemp]
  );

  const setTimeslots = (timeslots: TWorkingTime[]) => {
    const settings = JSON.parse(JSON.stringify(integrationSettingsTemp));
    settings.settings.timeslots = timeslots.map((timeslot: TWorkingTime): any => ({
      start_time: timeslot.startTime,
      close_time: timeslot.closeTime,
      weekday: getWeekdays().indexOf(timeslot.weekday),
    }));
    t360IntegrationStore.updateIntegrationSettingsTemp(props.integrationId, settings);
  };

  const onSaveTimeslot = (weekday: string, id: number | undefined, startTime: Date, closeTime: Date) => {
    const currDaySlot = {
      startTime: moment(startTime).format('HH:mm:ss'),
      closeTime: moment(closeTime).isBefore(startTime) ? '23:59:59' : moment(closeTime).format('HH:mm:ss'),
    };

    const selected = JSON.parse(JSON.stringify(selectedTimeslots));

    if (id === undefined) {
      selected.push({
        id: selected.length,
        weekday,
        startTime: currDaySlot.startTime,
        closeTime: currDaySlot.closeTime,
        isNew: true,
        menuGroupId: undefined,
        menuGroupName: undefined,
        orderType: undefined,
      });
    } else {
      selected.map((timeslot: TWorkingTime, idx: number) => {
        if (idx === id) {
          timeslot.weekday = weekday;
          timeslot.startTime = currDaySlot.startTime;
          timeslot.closeTime = currDaySlot.closeTime;
        }
        return timeslot;
      });
    }

    // handle next day slot
    if (moment(closeTime).isBefore(startTime)) {
      selected.push({
        id: selected.length,
        weekday: getNextDayOfWeek(weekday),
        startTime: '00:00:00',
        closeTime: moment(closeTime, 'HH:mm:ss').format('HH:mm:ss'),
        isNew: true,
        menuGroupId: undefined,
        menuGroupName: undefined,
        orderType: undefined,
      });
    }

    setTimeslots(selected);
  };

  const onDeleteTimeslot = (id: number | undefined) => {
    const selected = JSON.parse(JSON.stringify(selectedTimeslots));
    setTimeslots(selected.filter((timeslot: TBranchWorkingTime) => timeslot.id !== id));
  };

  return (
    <div className="t360-timeslots-container">
      <Text text="Working Time" type="section-header" className="mt-6 mb-4" />
      <div className="t360-timeslots-container__working-hours">
        {getWeekdays().map((weekday, idx) => (
          <WorkingHoursSlots
            key={idx}
            day={weekday}
            workingTime={selectedTimeslots}
            disabled={props.disabled || false}
            posIntAccess={RESTRICTION_ACCESS.ALLOWED}
            onSave={onSaveTimeslot}
            onDelete={onDeleteTimeslot}
            withoutMenuGroup
            withoutOrderType
          />
        ))}
      </div>
    </div>
  );
});

export default T360Timeslots;
